import { getCalApi } from "@calcom/embed-react";
import { CAL_NAMESPACE, CAL_TEAM_LINK } from "@/lib/cal-config";

export interface Referral {
  ref: string | null;
  src: string | null;
}

function readSession(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * The visitor's `?ref=` / `?src=` attribution. On the landing SPA the inline
 * script in index.html moves both into sessionStorage and removes them from
 * the URL before the app boots, so sessionStorage is the source of truth and
 * the URL is only a fallback (the /news shell has no such script).
 */
export function readReferral(): Referral {
  if (typeof window === "undefined") return { ref: null, src: null };
  const params = new URLSearchParams(window.location.search);
  return {
    ref: readSession("utm_ref") ?? params.get("ref")?.toLowerCase() ?? null,
    src: readSession("utm_src") ?? params.get("src")?.toLowerCase() ?? null,
  };
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Booking notes that tell the team who referred the lead and from where. */
export function buildPrefillNotes({ ref, src }: Referral): string | undefined {
  if (!ref && !src) return undefined;
  const parts: string[] = [];
  if (ref) parts.push(`Referred by ${capitalize(ref)}`);
  if (src) parts.push(`src: ${src}`);
  return parts.join(" · ");
}

/** Open the Cal.com booking modal from a "Talk to us" style CTA. */
export async function openBookACall(
  placement: string,
  label: string,
): Promise<void> {
  const referral = readReferral();
  window.gtag?.("event", "book_demo_click", { placement, label, ...referral });
  window.fbq?.("track", "Contact");

  const cal = await getCalApi({ namespace: CAL_NAMESPACE });
  const notes = buildPrefillNotes(referral);
  cal("modal", {
    calLink: CAL_TEAM_LINK,
    config: notes ? { notes } : undefined,
  });
}

/**
 * Report bookings completed in the Cal.com modal. The booking form lives in
 * Cal.com's iframe, so the embed's `bookingSuccessfulV2` event is the only
 * signal this page gets. Its payload (title, times, uid) stays out of
 * analytics, and Meta only ever gets the bare standard event.
 */
export async function trackCompletedBookings(): Promise<void> {
  const cal = await getCalApi({ namespace: CAL_NAMESPACE });
  cal("on", {
    action: "bookingSuccessfulV2",
    callback: () => {
      window.gtag?.("event", "book_demo_complete", readReferral());
      window.fbq?.("track", "Schedule");
    },
  });
}
